import { useEffect } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { logOut, userData } from "../../../redux/user/userSlice";
import { internalSignOut } from "@/api/LoginSignup";
import { checkLoginStatus } from "@/api/myProfile";
import { useLocation, useNavigate } from "react-router";
import { deleteCookie, setCookie } from "@/utils/cookies";
import {
  setLoginCallLocation,
  setLoginModal,
  setLoginTab,
  setShowContinueBtn,
  setShowSessionMsg,
  setShowTabsInModal,
} from "@/redux/login/loginSlice";
import { firebaseSignout } from "@/components/common/FirebaseApp";
import { setAuthenticated, clearAuthData } from "@/redux/auth/authSlice";
import { persistor } from "@/store/store";
import { clearAnalyticsCache, trackButtonClick } from "@/utils/analytics";
import { updateSaveProperty } from "@/redux/PDP/pdpSlice";

const useAuth = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const loginModal = useSelector((state) => state.login);
  const navigate = useNavigate();
  const location = useLocation();

  const isCiOfferPage = location.pathname.includes("ciOffer");

  const verifyLoginStatus = async () => {
    try {
      const response = await checkLoginStatus();

      // Clear analytics cache after login status change
      clearAnalyticsCache();

      if (response?.isLoggedOut) {
        dispatch(logOut({ isUserLoggedIn: false }));
        dispatch(updateSaveProperty("N"));
        deleteCookie("isLoggedIn");
      } else {
        dispatch(
          userData({
            userFirstName: response.userFirstName,
            userType: response.userType,
            isUserLoggedIn: true,
            user: response,
          })
        );
        setCookie("isLoggedIn", "true", 30); // Sets a cookie that expires in 30 days

        // A real user id is now in the store, so the temporary analytics id
        // cookie is obsolete — clear it so it can't override or linger.
        deleteCookie("gaUserId");

        // if (window.setGtagUserId && response.realbidUserId) {
        //   window.setGtagUserId(response.realbidUserId);
        // }
      }
    } catch (err) {
      dispatch(logOut({ isUserLoggedIn: false }));
      dispatch(updateSaveProperty("N"));
      dispatch(clearAuthData());
    }
  };

  const userSignOut = async ({
    isLoginModalRequired = false,
    navigationRequired = false,
  }) => {
    // Backend logout — but the client-side cleanup below must run even if this
    // fails/times out, otherwise the user id (store + gaUserId cookie) survives
    // and keeps getting attached to analytics events after "logout".
    try {
      await internalSignOut();
    } catch (err) {
      console.log("Error during backend logout -> ", err);
    }

    try {
      firebaseSignout();
      (navigationRequired || isCiOfferPage) && navigate("/");
      dispatch(logOut({ isUserLoggedIn: false }));
      dispatch(updateSaveProperty("N"));
      dispatch(clearAuthData());
      dispatch(setAuthenticated(false));
      dispatch(setShowSessionMsg(false));
      dispatch(setShowTabsInModal(true));
      dispatch(setShowContinueBtn(false));

      // Flush cleared auth state to localStorage so sell app sees it
      await persistor.flush();

      //clear cookies
      // document.cookie =
      //   "userEmail=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict";
      deleteCookie("isLoggedIn");
      // NOTE: do NOT delete visitorId on logout. It is the anonymous visitor
      // identifier and is meant to persist across login/logout so a device's
      // pre- and post-logout activity stays linked. (The previous
      // `Cookies.remove("visitorId")` here silently threw because Cookies was
      // never imported, so it never actually deleted it — the app has always
      // relied on visitorId surviving logout.)
      // Clear the temporary analytics id cookie so a stale user_uid is not
      // attached to a later/different user's events on this device. This MUST
      // happen before clearAnalyticsCache() and before the sign-out event fires,
      // otherwise getUserContext() would re-populate the cache from the cookie.
      deleteCookie("gaUserId");

      // Clear analytics cache after cookie removal so it can't be rebuilt from
      // a stale gaUserId cookie.
      clearAnalyticsCache();

      // // Clear CMS data
      // dispatch(clearCmsData());

      // // Optionally purge persisted store if needed
      // persistor.purge(); // Will clear all whitelisted persist data

      if (isLoginModalRequired) {
        batch(() => {
          dispatch(setLoginModal(true));
          dispatch(setLoginTab("loginTab"));
          dispatch(setLoginCallLocation("Popup"));
        });
      }

      trackButtonClick({
        eventName: "user_sign_out",
        eventCategory: "Sign-Out",
        eventSource: "Signout button click",
      });
    } catch (err) {
      console.log("Error during logout cleanup -> ", err);
    }
  };

  useEffect(() => {
    verifyLoginStatus();
  }, [loginModal?.loginModal]);

  return {
    isUserLoggedIn: user.isUserLoggedIn,
    userName: user.userName,
    userType: user.userType,
    userLastName: user.user.userLastName,
    userSignOut,
  };
};

export default useAuth;